import React, { useState, useEffect, useCallback } from 'react';
import { 
    Card, 
    Form, 
    Input, 
    Button, 
    Typography, 
    Tabs, 
    Upload, 
    message, 
    Row, 
    Col, 
    Divider,
    Space,
    Alert,
    Switch
} from 'antd';
import { 
    UploadOutlined, 
    SaveOutlined, 
    GlobalOutlined, 
    LayoutOutlined, 
    ShareAltOutlined,
    InfoCircleOutlined,
    FileTextOutlined,
    MailOutlined,
    EnvironmentOutlined,
    SafetyOutlined
} from '@ant-design/icons';
import brandingService from '../../services/brandingService';

const { Title, Text } = Typography;
const { TextArea } = Input;

const BrandingManagement = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [logoUrl, setLogoUrl] = useState('');
    const [fetching, setFetching] = useState(true);

    const fetchBranding = useCallback(async () => {
        try {
            setFetching(true);
            const response = await brandingService.getPublicBranding();
            if (response.success) {
                const data = response.data;
                // Pre-process JSON fields if necessary
                form.setFieldsValue({
                    branding_site_name: data.site_name,
                    branding_logo_url: data.logo_url,
                    branding_hero_title: data.hero_title,
                    branding_hero_subtitle: data.hero_subtitle,
                    branding_footer_about: data.footer_about,
                    branding_copyright: data.copyright,
                    branding_support_email: data.support_email,
                    branding_office_address: data.office_address,
                    // Social links
                    twitter: data.social_links?.twitter,
                    telegram: data.social_links?.telegram,
                    discord: data.social_links?.discord,
                    instagram: data.social_links?.instagram,
                    // Pages
                    page_terms_content: data.terms_content,
                    page_privacy_content: data.privacy_content,
                    page_aml_content: data.aml_content,
                    page_risk_content: data.risk_content,
                    page_about_content: data.about_content,
                    page_contact_content: data.contact_content,
                    page_security_content: data.security_content,
                    // Email
                    email_notifications_enabled: data.email_notifications_enabled,
                    smtp_host: data.smtp_host,
                    smtp_port: data.smtp_port,
                    smtp_user: data.smtp_user,
                    smtp_pass: data.smtp_pass,
                    smtp_from: data.smtp_from,
                    // Anti-fraud
                    anti_fraud_enabled: data.anti_fraud_enabled,
                    max_accounts_per_ip: data.max_accounts_per_ip,
                    block_vpn_registration: data.block_vpn_registration,
                    ip_intelligence_api_key: data.ip_intelligence_api_key,
                });
                setLogoUrl(data.logo_url);
            }
        } catch (error) {
            message.error('Erreur lors du chargement des paramètres');
        } finally {
            setFetching(false);
        }
    }, [form]);

    useEffect(() => {
        fetchBranding();
    }, [fetchBranding]);

    const handleUpload = async (info) => {
        if (info.file.status === 'uploading') {
            return;
        }
        try {
            const response = await brandingService.uploadFile(info.file.originFileObj);
            if (response.success) {
                const url = response.data.url;
                setLogoUrl(url);
                form.setFieldsValue({ branding_logo_url: url });
                message.success('Image téléchargée avec succès');
            }
        } catch (error) {
            message.error('Erreur lors du téléchargement');
        }
    };

    const onFinish = async (values) => {
        try {
            setLoading(true);
            const payload = {
                branding_site_name: values.branding_site_name,
                branding_logo_url: values.branding_logo_url,
                branding_hero_title: values.branding_hero_title,
                branding_hero_subtitle: values.branding_hero_subtitle,
                branding_footer_about: values.branding_footer_about,
                branding_copyright: values.branding_copyright,
                branding_support_email: values.branding_support_email,
                branding_office_address: values.branding_office_address,
                branding_social_links: {
                    twitter: values.twitter,
                    telegram: values.telegram,
                    discord: values.discord,
                    instagram: values.instagram
                },
                // Pages content
                page_terms_content: values.page_terms_content,
                page_privacy_content: values.page_privacy_content,
                page_aml_content: values.page_aml_content,
                page_risk_content: values.page_risk_content,
                page_about_content: values.page_about_content,
                page_contact_content: values.page_contact_content,
                page_security_content: values.page_security_content,
                // Email
                email_notifications_enabled: values.email_notifications_enabled,
                smtp_host: values.smtp_host,
                smtp_port: values.smtp_port,
                smtp_user: values.smtp_user,
                smtp_pass: values.smtp_pass,
                smtp_from: values.smtp_from,
                // Anti-fraud
                anti_fraud_enabled: values.anti_fraud_enabled,
                max_accounts_per_ip: values.max_accounts_per_ip,
                block_vpn_registration: values.block_vpn_registration,
                ip_intelligence_api_key: values.ip_intelligence_api_key,
            };

            const response = await brandingService.updateBranding(payload);
            if (response.success) {
                message.success('Paramètres mis à jour avec succès');
            }
        } catch (error) {
            message.error('Erreur lors de la mise à jour');
        } finally {
            setLoading(false);
        }
    };

    const items = [
        {
            key: '1',
            label: <span><GlobalOutlined /> Identité Visuelle</span>,
            children: (
                <div style={{ padding: '20px 0' }}>
                    <Row gutter={24}>
                        <Col span={16}>
                            <Form.Item
                                name="branding_site_name"
                                label="Nom du Site"
                                rules={[{ required: true }]}
                            >
                                <Input placeholder="Ex: NOVA Platform" />
                            </Form.Item>
                            
                            <Form.Item
                                name="branding_logo_url"
                                label="URL du Logo"
                            >
                                <Input placeholder="/uploads/logo.png" />
                            </Form.Item>

                            <div style={{ marginBottom: 20 }}>
                                <Text type="secondary">Ou téléchargez un nouveau logo :</Text>
                                <div style={{ marginTop: 10 }}>
                                    <Upload
                                        name="file"
                                        showUploadList={false}
                                        onChange={handleUpload}
                                        customRequest={({ file, onSuccess }) => {
                                            setTimeout(() => onSuccess("ok"), 0);
                                        }}
                                    >
                                        <Button icon={<UploadOutlined />}>Choisir un fichier</Button>
                                    </Upload>
                                </div>
                            </div>
                        </Col>
                        <Col span={8}>
                            <Card title="Aperçu du Logo" size="small" style={{ textAlign: 'center', background: 'rgba(255,255,255,0.02)' }}>
                                {logoUrl ? (
                                    <img src={logoUrl} alt="Logo Preview" style={{ maxWidth: '100%', maxHeight: 100 }} />
                                ) : (
                                    <div style={{ height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px dashed #444' }}>
                                        Aucun logo
                                    </div>
                                )}
                            </Card>
                        </Col>
                    </Row>
                </div>
            )
        },
        {
            key: '2',
            label: <span><LayoutOutlined /> Accueil (Hero)</span>,
            children: (
                <div style={{ padding: '20px 0' }}>
                    <Form.Item
                        name="branding_hero_title"
                        label="Titre Principal (Hero Title)"
                        rules={[{ required: true }]}
                    >
                        <TextArea rows={2} placeholder="Ex: Tradez. Investissez. Prospérez." />
                    </Form.Item>
                    <Form.Item
                        name="branding_hero_subtitle"
                        label="Sous-titre (Hero Subtitle)"
                        rules={[{ required: true }]}
                    >
                        <TextArea rows={3} />
                    </Form.Item>
                </div>
            )
        },
        {
            key: '3',
            label: <span><ShareAltOutlined /> Réseaux Sociaux</span>,
            children: (
                <div style={{ padding: '20px 0' }}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="twitter" label="Twitter (URL)">
                                <Input prefix={<GlobalOutlined />} placeholder="https://twitter.com/..." />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="telegram" label="Telegram (URL)">
                                <Input prefix={<GlobalOutlined />} placeholder="https://t.me/..." />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="discord" label="Discord (URL)">
                                <Input prefix={<GlobalOutlined />} placeholder="https://discord.gg/..." />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="instagram" label="Instagram (URL)">
                                <Input prefix={<GlobalOutlined />} placeholder="https://instagram.com/..." />
                            </Form.Item>
                        </Col>
                    </Row>
                </div>
            )
        },
        {
            key: '4',
            label: <span><InfoCircleOutlined /> Footer & Légal</span>,
            children: (
                <div style={{ padding: '20px 0' }}>
                    <Form.Item
                        name="branding_footer_about"
                        label="Texte 'À propos' du Footer"
                    >
                        <TextArea rows={3} />
                    </Form.Item>
                    <Form.Item
                        name="branding_copyright"
                        label="Texte de Copyright"
                    >
                        <Input />
                    </Form.Item>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="branding_support_email" label="Email de Support">
                                <Input prefix={<MailOutlined />} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="branding_office_address" label="Adresse (Siège Social)">
                                <Input prefix={<EnvironmentOutlined />} />
                            </Form.Item>
                        </Col>
                    </Row>
                </div>
            )
        },
        {
            key: '5',
            label: <span><FileTextOutlined /> Contenu des Pages</span>,
            children: (
                <div style={{ padding: '20px 0' }}>
                    <Tabs tabPosition="left" items={[
                        {
                            key: 'p1',
                            label: 'À propos',
                            children: <Form.Item name="page_about_content" label="Contenu Page À propos"><TextArea rows={10} /></Form.Item>
                        },
                        {
                            key: 'p2',
                            label: 'Conditions (CGU)',
                            children: <Form.Item name="page_terms_content" label="Contenu Page Terms"><TextArea rows={10} /></Form.Item>
                        },
                        {
                            key: 'p3',
                            label: 'Confidentialité',
                            children: <Form.Item name="page_privacy_content" label="Contenu Page Privacy"><TextArea rows={10} /></Form.Item>
                        },
                        {
                            key: 'p4',
                            label: 'AML / KYC',
                            children: <Form.Item name="page_aml_content" label="Contenu Page AML/KYC"><TextArea rows={10} /></Form.Item>
                        },
                        {
                            key: 'p5',
                            label: 'Risques',
                            children: <Form.Item name="page_risk_content" label="Contenu Page Risques"><TextArea rows={10} /></Form.Item>
                        },
                        {
                            key: 'p6',
                            label: 'Sécurité',
                            children: <Form.Item name="page_security_content" label="Contenu Page Sécurité"><TextArea rows={10} /></Form.Item>
                        },
                        {
                            key: 'p7',
                            label: 'Contact',
                            children: <Form.Item name="page_contact_content" label="Contenu Page Contact"><TextArea rows={10} /></Form.Item>
                        }
                    ]} />
                </div>
            )
        },
        {
            key: '6',
            label: <span><MailOutlined /> Serveur Email (SMTP)</span>,
            children: (
                <div style={{ padding: '20px 0' }}>
                    <Alert 
                        message="Configuration SMTP"
                        description="Ces paramètres sont utilisés pour l'envoi des emails de réinitialisation de mot de passe et les notifications système."
                        type="info"
                        showIcon
                        style={{ marginBottom: 24 }}
                    />
                    
                    <Form.Item name="email_notifications_enabled" label="Activer les notifications par Email" valuePropName="checked">
                        <Switch />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={18}>
                            <Form.Item name="smtp_host" label="Hôte SMTP (ex: smtp.gmail.com)">
                                <Input placeholder="smtp.votre-serveur.com" />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item name="smtp_port" label="Port">
                                <Input placeholder="465 ou 587" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="smtp_user" label="Utilisateur SMTP">
                                <Input placeholder="contact@votre-domaine.com" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="smtp_pass" label="Mot de passe SMTP">
                                <Input.Password placeholder="Votre mot de passe" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="smtp_from" label="Email de l'expéditeur (From)">
                        <Input placeholder="NOVA Platform <noreply@votre-domaine.com>" />
                    </Form.Item>
                </div>
            )
        },
        {
            key: '7',
            label: <span><SafetyOutlined /> Sécurité & Anti-Fraude</span>,
            children: (
                <div style={{ padding: '20px 0' }}>
                    <Alert 
                        message="Protection Multi-Compte"
                        description="Ces paramètres permettent de bloquer les utilisateurs qui tentent de créer plusieurs comptes pour abuser des bonus."
                        type="warning"
                        showIcon
                        style={{ marginBottom: 24 }}
                    />
                    
                    <Form.Item name="anti_fraud_enabled" label="Activer le système Anti-Fraude" valuePropName="checked">
                        <Switch />
                    </Form.Item>

                    <Form.Item name="max_accounts_per_ip" label="Nombre maximum de comptes par IP">
                        <Input type="number" min={1} style={{ width: 200 }} />
                    </Form.Item>

                    <Divider />

                    <h3 className="text-lg font-bold mb-4">Détection VPN & Proxy</h3>
                    <Alert 
                        message="Service d'Intelligence IP"
                        description="La détection VPN nécessite une clé API tierce (ex: IPHub). Si activé, les inscriptions depuis un VPN seront bloquées."
                        type="info"
                        style={{ marginBottom: 16 }}
                    />

                    <Form.Item name="block_vpn_registration" label="Bloquer les inscriptions via VPN" valuePropName="checked">
                        <Switch />
                    </Form.Item>

                    <Form.Item name="ip_intelligence_api_key" label="Clé API (IP Intelligence)">
                        <Input.Password placeholder="Clé API IPHub ou ipstack" />
                    </Form.Item>
                </div>
            )
        }
    ];

    return (
        <div className="branding-management-container">
            <div style={{ marginBottom: 24 }}>
                <Title level={2} style={{ color: '#f1f5f9', margin: 0 }}>
                    Gestion du Contenu & Branding
                </Title>
                <Text type="secondary">Personnalisez l'apparence et les textes principaux de votre plateforme.</Text>
            </div>

            <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                disabled={fetching}
            >
                <Card className="glass-card">
                    <Tabs defaultActiveKey="1" items={items} />
                    
                    <Divider />
                    
                    <div style={{ textAlign: 'right' }}>
                        <Space>
                            <Button onClick={fetchBranding} disabled={loading}>
                                Réinitialiser
                            </Button>
                            <Button 
                                type="primary" 
                                htmlType="submit" 
                                icon={<SaveOutlined />} 
                                loading={loading}
                                style={{ background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', border: 'none', borderRadius: 8, height: 40, padding: '0 30px' }}
                            >
                                Enregistrer les modifications
                            </Button>
                        </Space>
                    </div>
                </Card>
            </Form>

            <style dangerouslySetInnerHTML={{ __html: `
                .glass-card {
                    background: rgba(30, 41, 59, 0.4) !important;
                    backdrop-filter: blur(12px);
                    border: 1px solid rgba(255, 255, 255, 0.05) !important;
                    border-radius: 16px !important;
                    box-shadow: 0 8px 32px 0 rgba(0, 0, 0, 0.37);
                }
                .ant-tabs-nav::before {
                    border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
                }
                .ant-tabs-tab {
                    color: #94a3b8 !important;
                }
                .ant-tabs-tab-active .ant-tabs-tab-btn {
                    color: #6366f1 !important;
                }
                .ant-tabs-ink-bar {
                    background: #6366f1 !important;
                }
                label {
                    color: #e2e8f0 !important;
                }
            `}} />
        </div>
    );
};

export default BrandingManagement;
